import { collection, doc, onSnapshot, query, where, setDoc, updateDoc, runTransaction, getDocFromServer } from 'firebase/firestore';
import { db, offlinePersistenceReady } from '../firebaseConfig';

const actionRef = action => doc(db, 'dashboardOfflineActions', action.actorUid, 'actions', action.operationId);

export const firestoreQueue = {
  async ready() {
    if (!await offlinePersistenceReady) throw new Error('Persistent storage is unavailable. Keep the form open and retry online.');
  },
  async enqueue(action, onLateError) {
    await this.ready();
    const reference = actionRef(action);
    // setDoc resolves only after server acknowledgement. Observe the local
    // snapshot instead, so an offline save does not wait for internet.
    await new Promise((resolve, reject) => {
      let stop = () => {};
      let settled = false;
      const finish = callback => { if (!settled) { settled = true; clearTimeout(timer); stop(); callback(); } };
      const timer = setTimeout(() => finish(() => reject(new Error('Local save could not be confirmed. Keep the form open.'))), 10000);
      stop = onSnapshot(reference, { includeMetadataChanges: true }, snapshot => {
        if (snapshot.exists() && snapshot.data().operationId === action.operationId) finish(resolve);
      }, error => finish(() => reject(error)));
      const data = { ...action };
      delete data.cloudConfirmed;
      void setDoc(reference, data).catch(error => {
        if (!settled) finish(() => reject(error));
        else onLateError(action, error);
      });
    });
  },
  async inspect(action) {
    const snapshot = await getDocFromServer(actionRef(action));
    return snapshot.exists() ? snapshot.data() : null;
  },
  subscribe(uid, listener, onError) {
    return onSnapshot(query(collection(db, 'dashboardOfflineActions', uid, 'actions'), where('status', '!=', 'applied')),
      { includeMetadataChanges: true }, snapshot => {
        listener(snapshot.docs.map(item => ({ ...item.data(), cloudConfirmed: !item.metadata.hasPendingWrites && !snapshot.metadata.fromCache })));
      }, onError);
  },
  // Status is a client progress hint. The MongoDB receipt is authoritative.
  update(action, patch, onError) { void updateDoc(actionRef(action), patch).catch(onError); },
  async claim(action, owner, now = Date.now()) {
    return runTransaction(db, async transaction => {
      const snapshot = await transaction.get(actionRef(action));
      if (!snapshot.exists()) return false;
      const current = snapshot.data();
      if (!['pending', 'retry'].includes(current.status) || current.nextAttemptAt > now || current.leaseUntil > now) return false;
      transaction.update(actionRef(action), { leaseOwner: owner, leaseUntil: now + 60000 });
      return true;
    });
  },
};
